/* components/patterns/Tabs.jsx — навигация по вкладкам и якорям.
   ScrollRow — горизонтально прокручиваемая строка с edge-fade по краям.
   Tabs      — варианты line/pills/segmented; при переполнении → ScrollRow.
   AnchorNav — sticky навигация по якорям раздела (TTKEditor 1–7); smooth-scroll.
   ХЕНДОФФ → frontend/components/common/. */

import React from 'react';
import { Icon } from '../ui/Icon.jsx';

  const { useState, useRef, useEffect } = React;

  /* ── ScrollRow: edge-fade при переполнении ── */
  function ScrollRow({ children, className, gap }) {
    const ref = useRef(null);
    const [edge, setEdge] = useState({ l: false, r: false });
    const update = () => {
      const el = ref.current; if (!el) return;
      setEdge({ l: el.scrollLeft > 4, r: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
    };
    useEffect(() => {
      update();
      const el = ref.current; if (!el) return;
      el.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      return () => { el.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
    }, [children]);
    return (
      <div className={'scroll-row-wrap' + (edge.l ? ' fade-l' : '') + (edge.r ? ' fade-r' : '') + (className ? ' ' + className : '')}>
        <div className="scroll-row" ref={ref} style={gap ? { gap } : undefined}>{children}</div>
      </div>
    );
  }

  /* ── Tabs ── */
  function Tabs({ items = [], value, onChange, variant = 'line', className, ariaLabel = 'Вкладки' }) {
    const inner = (
      <div className={'tabs tabs--' + variant} role="tablist" aria-label={ariaLabel}>
        {items.map((it) => {
          const t = typeof it === 'string' ? { value: it, label: it } : it;
          const active = t.value === value;
          return (
            <button type="button" key={t.value} role="tab" aria-selected={active}
              className={'tab' + (active ? ' is-active' : '')} onClick={() => onChange && onChange(t.value)}>
              {t.icon && <Icon name={t.icon} size={16} />}
              <span className="tab__label">{t.label}</span>
              {t.count != null && <span className="tab__count num">{t.count}</span>}
            </button>
          );
        })}
      </div>
    );
    return <ScrollRow className={'tabs-scroll tabs-scroll--' + variant + (className ? ' ' + className : '')}>{inner}</ScrollRow>;
  }

  /* ── AnchorNav: sticky по якорям ── */
  function AnchorNav({ items = [], orientation = 'vertical', offset = 80, className }) {
    const [active, setActive] = useState(items[0] && items[0].id);
    useEffect(() => {
      const ids = items.map((i) => i.id);
      const onScroll = () => {
        let cur = ids[0];
        for (const id of ids) {
          const el = document.getElementById(id);
          if (el && el.getBoundingClientRect().top - offset <= 1) cur = id;
        }
        setActive(cur);
      };
      const scroller = document.querySelector('[data-scroll-main]') || window;
      scroller.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      return () => scroller.removeEventListener('scroll', onScroll);
    }, [items, offset]);
    const go = (e, id) => {
      e.preventDefault();
      const el = document.getElementById(id);
      if (el) { const top = el.getBoundingClientRect().top + window.scrollY - offset; window.scrollTo({ top, behavior: 'smooth' }); }
      setActive(id);
    };
    return (
      <nav className={'anchor-nav anchor-nav--' + orientation + (className ? ' ' + className : '')} aria-label="Разделы">
        {items.map((it) => (
          <a key={it.id} href={'#' + it.id} onClick={(e) => go(e, it.id)}
            className={'anchor-nav__item' + (active === it.id ? ' is-active' : '')} aria-current={active === it.id ? 'true' : undefined}>
            {it.num != null && <span className="anchor-nav__num num">{it.num}</span>}
            <span className="anchor-nav__label">{it.label}</span>
          </a>
        ))}
      </nav>
    );
  }

export { ScrollRow };
export { Tabs };
export { AnchorNav };
